import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileCheck2, Search, Loader2, Download, ExternalLink, CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const STATUS_STYLES = {
  issued:       { label: 'Issued',        className: 'text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10', Icon: Clock },
  acknowledged: { label: 'Acknowledged',  className: 'text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10', Icon: CheckCircle2 },
  rejected:     { label: 'Rejected',      className: 'text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10', Icon: XCircle },
  superseded:   { label: 'Superseded',    className: 'text-[#94A3B8] border-[#1E293B] bg-white/5',         Icon: RefreshCw },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

/** Admin-only tab: every FCO ever issued, joined with parties + mineral. */
export default function FCOsAdminTab({ onCountChange }) {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const r = await axios.get(`${API_URL}/admin/fcos`,
        { headers: getAuthHeader(), params });
      setRows(r.data.fcos || []);
      setBuckets(r.data.by_status || {});
      onCountChange?.((r.data.by_status?.issued) || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load FCOs');
    } finally { setLoading(false); }
  }, [getAuthHeader, statusFilter, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return rows;
    return rows.filter((r) => (
      [r.fco_number, r.deal_id, r.seller?.name, r.seller?.email,
       r.buyer?.name, r.buyer?.email, r.mineral?.title,
       r.commodity?.origin, r.commodity?.name]
      .some((v) => (v || '').toString().toLowerCase().includes(q))
    ));
  }, [rows, q]);

  const download = async (fco) => {
    try {
      const r = await axios.get(`${API_URL}/deals/${fco.deal_id}/fco.pdf`,
        { headers: getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${fco.fco_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = err.message || 'Download failed';
      if (err.response?.data instanceof Blob) {
        try {
          const t = await err.response.data.text();
          msg = (JSON.parse(t)?.detail) || msg;
        } catch { /* keep msg */ }
      }
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-fcos-tab">
      {/* Status counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['issued', 'acknowledged', 'rejected', 'superseded'].map((k) => {
          const s = STATUS_STYLES[k]; const Icon = s.Icon;
          return (
            <Card key={k}
                  className={`bg-[#121A2F] border-[#1E293B] p-4 cursor-pointer transition-colors hover:border-[#F59E0B]/40 ${statusFilter === k ? 'border-[#F59E0B]' : ''}`}
                  onClick={() => setStatusFilter(statusFilter === k ? '' : k)}
                  data-testid={`admin-fco-bucket-${k}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{s.label}</p>
                  <p className="text-2xl text-white font-semibold mt-0.5">{buckets[k] ?? 0}</p>
                </div>
                <Icon className={`w-6 h-6 ${s.className.split(' ')[0]}`} />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Search + reset */}
      <Card className="bg-[#121A2F] border-[#1E293B] p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-[#64748B]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search FCO number, buyer, seller, mineral, origin…"
                   data-testid="admin-fco-search"
                   className="pl-9 bg-[#0B1120] border-[#1E293B] text-white h-9 text-sm" />
          </div>
          <span className="text-xs text-[#94A3B8]">
            Showing <b className="text-white">{filtered.length}</b> of {rows.length}
            {statusFilter && (
              <button className="ml-2 text-[#F59E0B] hover:underline"
                      onClick={() => setStatusFilter('')} data-testid="admin-fco-clear-status">
                clear "{STATUS_STYLES[statusFilter]?.label}" filter
              </button>
            )}
          </span>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-[#121A2F] border-[#1E293B] overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#F59E0B]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-[#64748B]">
            <FileCheck2 className="w-10 h-10 mx-auto mb-2 text-[#1E293B]" />
            <p className="text-sm">No FCOs match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-fco-table">
              <thead className="bg-[#1A2440] border-b border-[#1E293B]">
                <tr>
                  {['FCO', 'Seller', 'Buyer', 'Mineral', 'Value', 'Status', 'Issued / Ack', 'Actions'].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-left text-xs uppercase tracking-wider text-[#94A3B8] whitespace-nowrap ${i === 7 ? 'sticky right-0 bg-[#1A2440]' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const s = STATUS_STYLES[f.status] || STATUS_STYLES.issued; const Icon = s.Icon;
                  const ack = f.acknowledgement?.acknowledged_at;
                  const rej = f.rejection?.rejected_at;
                  return (
                    <tr key={f.id} className="border-b border-[#1E293B] last:border-0 hover:bg-white/[0.02]"
                        data-testid={`admin-fco-row-${f.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-white font-mono" data-testid={`admin-fco-number-${f.id}`}>{f.fco_number}</p>
                        <p className="text-[10px] text-[#64748B]">Deal {f.deal_id?.slice(0, 8)}…</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white">{f.seller?.name || '—'}</p>
                        <p className="text-[10px] text-[#64748B]">{f.seller?.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white">{f.buyer?.name || '—'}</p>
                        <p className="text-[10px] text-[#64748B]">{f.buyer?.email}</p>
                      </td>
                      <td className="px-4 py-3 text-[#94A3B8] max-w-[200px] truncate">
                        {f.mineral?.title || f.commodity?.name || '—'}
                        <p className="text-[10px] text-[#64748B]">{f.commodity?.origin || ''}</p>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[#F59E0B] font-semibold">
                        {(f.commercial?.currency || 'USD')} {(f.commercial?.total_value || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.className}`}
                              data-testid={`admin-fco-status-${f.id}`}>
                          <Icon className="w-3 h-3" />{s.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-[10px] text-[#94A3B8]">
                        <p>Issued: {fmt(f.issued_at)}</p>
                        {ack && <p className="text-[#10B981]">Ack: {fmt(ack)}</p>}
                        {rej && <p className="text-[#EF4444]">Rej: {fmt(rej)}</p>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap sticky right-0 bg-[#121A2F]">
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline"
                                  onClick={() => setDetail(f)}
                                  data-testid={`admin-fco-view-${f.id}`}
                                  className="bg-transparent border-[#1E293B] text-white h-8 px-2">
                            View
                          </Button>
                          <Button size="sm" variant="outline"
                                  onClick={() => download(f)}
                                  data-testid={`admin-fco-download-${f.id}`}
                                  className="bg-transparent border-[#1E293B] text-white h-8 px-2">
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline"
                                  onClick={() => window.open(`/deals/${f.deal_id}`, '_blank')}
                                  data-testid={`admin-fco-open-deal-${f.id}`}
                                  className="bg-transparent border-[#1E293B] text-white h-8 px-2">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail dialog — full FCO content */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl bg-[#121A2F] border-[#1E293B] text-white" data-testid="admin-fco-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-[#F59E0B]" />
              {detail?.fco_number}
            </DialogTitle>
          </DialogHeader>
          {detail && <AdminFCODetail fco={detail} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AdminFCODetail = ({ fco }) => {
  const s = STATUS_STYLES[fco.status] || STATUS_STYLES.issued;
  const Icon = s.Icon;
  const c = fco.commercial || {};
  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto" data-testid="admin-fco-detail-body">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${s.className}`}>
          <Icon className="w-3.5 h-3.5" />{s.label}
        </span>
        <span className="text-[10px] text-[#64748B]">
          Deal: {fco.deal_id} · Issued {fmt(fco.issued_at)} · Valid until {fmt(fco.valid_until)}
        </span>
      </div>

      <Section title="Parties">
        <Row k="Seller" v={`${fco.seller?.name || '—'} · ${fco.seller?.email || ''}${fco.seller?.company ? ' · ' + fco.seller.company : ''}`} />
        <Row k="Buyer"  v={`${fco.buyer?.name  || '—'} · ${fco.buyer?.email  || ''}${fco.buyer?.company  ? ' · ' + fco.buyer.company  : ''}`} />
      </Section>

      <Section title="Commodity">
        <Row k="Description" v={fco.commodity?.name || '—'} />
        <Row k="Origin"      v={fco.commodity?.origin || '—'} />
      </Section>

      {(fco.specs || []).length > 0 && (
        <Section title="Specifications">
          <table className="w-full text-xs">
            <thead className="text-[#94A3B8] uppercase text-[10px]">
              <tr><th className="text-left py-1">Parameter</th><th className="text-left py-1">Typical</th><th className="text-left py-1">Rejection</th></tr>
            </thead>
            <tbody>
              {fco.specs.map((sp) => (
                <tr key={sp.key} className="border-t border-[#1E293B]">
                  <td className="py-1.5 text-white">{sp.label}</td>
                  <td className="py-1.5 text-[#F59E0B]">{sp.value}{sp.unit ? ` ${sp.unit}` : ''}</td>
                  <td className="py-1.5 text-[#94A3B8]">{sp.rejection || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      <Section title="Commercial">
        <Row k="Quantity"      v={`${c.quantity} ${c.unit}`} />
        <Row k="Price"         v={`${c.currency || 'USD'} ${(c.price_per_unit || 0).toLocaleString()} / ${c.unit}`} />
        <Row k="Total"         v={`${c.currency || 'USD'} ${(c.total_value || 0).toLocaleString()}`} highlight />
        <Row k="Incoterm"      v={c.incoterm} />
        <Row k="Loading Port"  v={c.loading_port || '—'} />
        <Row k="Discharge Port" v={c.discharge_port || '—'} />
        <Row k="Payment Terms" v={c.payment_terms} multiline />
      </Section>

      {fco.procedure && (
        <Section title="Transaction Procedure">
          <pre className="whitespace-pre-wrap text-xs text-[#94A3B8] font-sans">{fco.procedure}</pre>
        </Section>
      )}

      <Section title="Acknowledgement / Rejection">
        {fco.acknowledgement?.acknowledged_at ? (
          <Row k="Acknowledged" v={`${fco.acknowledgement.buyer_name || 'Buyer'} on ${fmt(fco.acknowledgement.acknowledged_at)}`} highlightGreen />
        ) : fco.rejection?.rejected_at ? (
          <>
            <Row k="Rejected on" v={fmt(fco.rejection.rejected_at)} highlightRed />
            <Row k="Reason"      v={fco.rejection.reason || '—'} multiline />
          </>
        ) : (
          <p className="text-xs text-[#94A3B8]">
            {fco.status === 'issued' && !fco.is_expired
              ? `Awaiting buyer response — ${Math.floor((fco.seconds_remaining || 0) / 3600)}h left of the 7-day window.`
              : fco.status === 'superseded'
                ? 'Superseded by a later FCO on the same deal.'
                : fco.is_expired ? 'Validity window expired without a response.' : ''}
          </p>
        )}
      </Section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="rounded-md border border-[#1E293B] bg-white/[0.02] p-3">
    <p className="text-[10px] uppercase tracking-wider text-[#F59E0B] mb-2">{title}</p>
    <div className="space-y-1">{children}</div>
  </div>
);
const Row = ({ k, v, highlight, highlightGreen, highlightRed, multiline }) => (
  <div className={`flex ${multiline ? 'flex-col gap-0.5' : 'items-start justify-between gap-4'} text-xs`}>
    <span className="text-[#94A3B8]">{k}</span>
    <span className={
      `${multiline ? 'text-white whitespace-pre-wrap' : 'text-right'} ` +
      `${highlight ? 'text-[#F59E0B] font-semibold' : ''} ` +
      `${highlightGreen ? 'text-[#10B981] font-semibold' : ''} ` +
      `${highlightRed ? 'text-[#EF4444] font-semibold' : ''} ` +
      (highlight || highlightGreen || highlightRed || multiline ? '' : 'text-white')
    }>{v}</span>
  </div>
);
